import {isQuestionRegionId, questionRegionRank} from './annotationRegions';
import {selectNotedMarksInOrder} from './annotationSelectors';
import type {Annotation} from './annotationTypes';

/** A noted mark with the bucket it lives in, which addressing the store needs. */
export interface QuestionNote {
  regionId: string;
  annotation: Annotation;
}

/**
 * Every noted mark on `questionId`, in reading order: the prompt's, then the mounted
 * supplements' (their regions are question-free, so which belong here is the mount
 * registry's answer), then the remaining tiers. Allocates — call from a `useMemo`.
 */
export function collectQuestionNotes(
  annotationsByRegionId: Record<string, Annotation[]>,
  questionId: string | undefined,
  supplementRegionIds: string[]
): QuestionNote[] {
  if (!questionId) {
    return [];
  }
  const questionEntries = Object.entries(annotationsByRegionId)
    .filter(([regionId]) => isQuestionRegionId(regionId, questionId))
    .sort(([a], [b]) => questionRegionRank(a) - questionRegionRank(b));
  const promptEntries = questionEntries.filter(([regionId]) => questionRegionRank(regionId) === 0);
  const laterEntries = questionEntries.filter(([regionId]) => questionRegionRank(regionId) > 0);
  const supplementEntries = supplementRegionIds.map(
    (regionId) => [regionId, annotationsByRegionId[regionId] ?? []] as const
  );
  return [...promptEntries, ...supplementEntries, ...laterEntries].flatMap(
    ([regionId, annotations]) =>
      selectNotedMarksInOrder(annotations).map((annotation) => ({regionId, annotation}))
  );
}
